
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service';

const Register: React.FC = () => {
  const [formData, setFormData] = useState({
    fname: '',
    lname: '',
    email: '',
    phone: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const response = await authService.register(formData);
      if (response.status) {
        navigate('/login');
      } else {
        setError(response.message || 'Registration failed');
      }
    } catch (err: any) {
      setError(err.message || 'Unable to connect to server');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4 py-12 relative overflow-hidden">
      {/* Cinematic Background Glows */}
      <div className="absolute top-1/4 -left-20 w-96 h-96 bg-purple-600/10 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-indigo-600/10 rounded-full blur-[120px] pointer-events-none"></div>

      <div className="max-w-lg w-full space-y-8 glass-panel p-10 lg:p-12 rounded-[2.5rem] border border-slate-800 shadow-2xl relative z-10 animate-in fade-in zoom-in duration-700">
        <div className="text-center">
          {/* New AICW Hex-Core Logo */}
          <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-purple-500 to-indigo-600 mb-6 shadow-2xl shadow-purple-600/20">
            <svg className="w-12 h-12 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L3 7V17L12 22L21 17V7L12 2Z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round"/>
              <path d="M12 6L7 15H9.5L12 10L14.5 15H17L12 6Z" fill="currentColor"/>
              <circle cx="12" cy="13.5" r="1.2" fill="white" className="animate-pulse" />
              <path d="M8 18L12 16L16 18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 className="text-3xl font-black text-white tracking-tight uppercase">Create Account</h2>
          <p className="mt-2 text-[10px] text-purple-400 font-bold uppercase tracking-[0.2em]">Start your trading journey with AICW</p>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-500 text-[11px] p-4 rounded-2xl text-center font-black uppercase tracking-widest animate-in slide-in-from-top-2">
            {error}
          </div>
        )}

        <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="group">
              <label className="block text-[10px] font-black text-slate-500 group-focus-within:text-purple-400 uppercase tracking-widest ml-1 mb-2 transition-colors">First Name</label>
              <input
                name="fname"
                type="text"
                required
                value={formData.fname}
                onChange={handleChange}
                className="block w-full px-6 py-4 bg-slate-950/50 border border-slate-800 rounded-2xl text-white focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all outline-none font-bold placeholder-slate-700 text-sm"
                placeholder="John"
              />
            </div>
            <div className="group">
              <label className="block text-[10px] font-black text-slate-500 group-focus-within:text-purple-400 uppercase tracking-widest ml-1 mb-2 transition-colors">Last Name</label>
              <input
                name="lname"
                type="text"
                required
                value={formData.lname}
                onChange={handleChange}
                className="block w-full px-6 py-4 bg-slate-950/50 border border-slate-800 rounded-2xl text-white focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all outline-none font-bold placeholder-slate-700 text-sm"
                placeholder="Doe"
              />
            </div>
          </div>
          <div className="group">
            <label className="block text-[10px] font-black text-slate-500 group-focus-within:text-purple-400 uppercase tracking-widest ml-1 mb-2 transition-colors">Email Address</label>
            <input
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              className="block w-full px-6 py-4 bg-slate-950/50 border border-slate-800 rounded-2xl text-white focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all outline-none font-bold placeholder-slate-700 text-sm"
              placeholder="example@email.com"
            />
          </div>
          <div className="group">
            <label className="block text-[10px] font-black text-slate-500 group-focus-within:text-purple-400 uppercase tracking-widest ml-1 mb-2 transition-colors">Phone Number</label>
            <input
              name="phone"
              type="tel"
              required
              value={formData.phone}
              onChange={handleChange}
              className="block w-full px-6 py-4 bg-slate-950/50 border border-slate-800 rounded-2xl text-white focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all outline-none font-bold placeholder-slate-700 text-sm"
              placeholder="+1234567890"
            />
          </div>
          <div className="group">
            <label className="block text-[10px] font-black text-slate-500 group-focus-within:text-purple-400 uppercase tracking-widest ml-1 mb-2 transition-colors">Password</label>
            <div className="relative">
              <input
                name="password"
                type={showPassword ? "text" : "password"}
                required
                value={formData.password}
                onChange={handleChange}
                className="block w-full px-6 py-4 bg-slate-950/50 border border-slate-800 rounded-2xl text-white focus:ring-2 focus:ring-purple-600 focus:border-transparent transition-all outline-none pr-14 font-bold placeholder-slate-700 text-sm"
                placeholder="Create a password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-600 hover:text-purple-400 transition-colors p-2"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268-2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268-2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex justify-center py-4 px-6 bg-purple-600 hover:bg-purple-500 text-white font-black rounded-2xl transition-all shadow-2xl shadow-purple-600/30 active:scale-95 disabled:opacity-50 uppercase tracking-[0.2em] text-xs mt-4"
          >
            {isSubmitting ? 'Registering...' : 'Register Now'}
          </button>
        </form>

        <p className="text-center text-[10px] text-slate-500 font-bold uppercase tracking-widest pt-4 border-t border-white/5">
          Already have an account?{' '}
          <Link to="/login" className="font-bold text-purple-500 hover:text-purple-400 transition-colors ml-1">
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
